import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { STRIPE_PLANS, DEFAULT_TEST_PRICE_ID, STRIPE_TEST_CONFIG, STRIPE_LIVE_CONFIG, validatePriceId, STRIPE_BILLING_PORTAL_LOGIN_URL } from '../config/stripe-pricing';
import { collection, addDoc, doc, onSnapshot, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/** Detecta se o erro é de cliente offline ou erro interno do Firebase (não adianta retentar). */
function isOfflineOrInternalError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; message?: string };
  const code = (e.code ?? '').toString().toLowerCase();
  const msg = (e.message || '').toLowerCase();
  return (
    code === 'unavailable' ||
    code === 'internal' ||
    code.includes('internal') || // functions/internal, etc.
    msg.includes('internal') ||
    msg.includes('client is offline') ||
    msg.includes('failed to get document')
  );
}

// Configurações do Stripe - MODO DE PRODUÇÃO REAL ATIVO
const STRIPE_CONFIG = {
  publishableKey: STRIPE_LIVE_CONFIG.publishableKey,
  apiVersion: '2023-10-16' as const,
  testMode: false, // MODO DE PRODUÇÃO ATIVO - USAR STRIPE REAL
  environment: 'production' // AMBIENTE DE PRODUÇÃO
};

// Interface para os dados do checkout
interface CheckoutData {
  priceId: string;
  customerEmail?: string;
  successUrl?: string;
  cancelUrl?: string;
}

// Interface para resposta do checkout
interface CheckoutResponse {
  sessionId: string;
  url: string;
}

// Interface para resposta do portal
interface PortalResponse {
  url: string;
}

// Classe do serviço Stripe
class StripeService {
  private functions = getFunctions(undefined, 'us-central1'); // Especificar região para evitar problemas de CORS
  private auth = getAuth();

  // Função para validar e sanitizar price ID
  private sanitizePriceId(priceId: string): string {
    console.log('🔍 [STRIPE-SERVICE] Validando Price ID:', priceId);
    
    // Se o price ID não for válido, usar o padrão starter
    if (!validatePriceId(priceId)) {
      console.warn('⚠️ [STRIPE-SERVICE] Price ID inválido, usando padrão starter:', priceId);
      return DEFAULT_TEST_PRICE_ID;
    }
    
    return priceId;
  }

  // Função para criar sessão de checkout
  async createCheckoutSession(data: CheckoutData): Promise<CheckoutResponse> {
    console.log('🚀 [STRIPE-SERVICE] MODO PRODUÇÃO - Iniciando checkout real:', data);
    console.log('💳 [STRIPE-SERVICE] Configuração:', {
      environment: STRIPE_CONFIG.environment,
      testMode: STRIPE_CONFIG.testMode,
      publishableKey: STRIPE_CONFIG.publishableKey.substring(0, 20) + '...'
    });

    // Validar dados
    if (!data.priceId) {
      throw new Error('Price ID é obrigatório');
    }

    // Sanitizar price ID
    const safePriceId = this.sanitizePriceId(data.priceId);
    console.log('✅ [STRIPE-SERVICE] Price ID sanitizado:', safePriceId);

    // Verificar se o usuário está autenticado
    const user = this.auth.currentUser;
    const customerEmail = data.customerEmail || user?.email || 'demo@upclinic.com';

    // Dados seguros para checkout
    const checkoutData = {
      ...data,
      priceId: safePriceId,
      customerEmail: customerEmail
    };

    // MODO PRODUÇÃO: Usar Stripe real para vendas
    try {
      // MÉTODO 1: Usar Firebase Stripe Extension (PRODUÇÃO REAL)
      console.log('🔥 [STRIPE-SERVICE] Usando Firebase Stripe Extension para vendas reais...');
      return await this.createFirebaseStripeCheckout(checkoutData);
    } catch (error) {
      console.error('❌ [STRIPE-SERVICE] Erro na Firebase Stripe Extension:', error);
      
      try {
        // MÉTODO 2: Usar Firebase Functions como fallback
        console.log('⚡ [STRIPE-SERVICE] Tentando Firebase Functions...');
        return await this.createFirebaseFunctionCheckout(checkoutData);
      } catch (error2) {
        console.error('❌ [STRIPE-SERVICE] Erro nas Firebase Functions:', error2);
        
        // MÉTODO 3: Checkout direto como emergência
        console.log('🆘 [STRIPE-SERVICE] Usando checkout direto como emergência...');
        return await this.createDirectStripeCheckout(checkoutData);
      }
    }
  }

  // MÉTODO 1: Simulação direta para teste
  private async createSimulatedCheckout(data: CheckoutData): Promise<CheckoutResponse> {
    console.log('🎭 [SIMULAÇÃO] Criando checkout simulado para desenvolvimento');
    
    // Encontrar plano pelo price ID
    const plan = this.findPlanByPriceId(data.priceId);
    const planName = plan?.name || 'Plano Selecionado';
    const planPrice = plan?.monthly?.price || plan?.annual?.price || 0;
    const billingType = plan?.monthly?.priceId === data.priceId ? 'Mensal' : 'Anual';
    
    // Simular delay do servidor
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Gerar session ID simulado
    const mockSessionId = `cs_test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const successUrl = data.successUrl || `${window.location.origin}/success?session_id=${mockSessionId}`;
    
    console.log('✅ [SIMULAÇÃO] Checkout simulado criado:', {
      sessionId: mockSessionId,
      plan: planName,
      priceId: data.priceId,
      price: planPrice,
      billing: billingType
    });

    // Redirecionamento direto sem modal
    console.log('🛠️ [SIMULATED] Redirecionando para página de sucesso...');

    return {
      sessionId: mockSessionId,
      url: successUrl
    };
  }

  // MÉTODO 2: Firebase Stripe Extension
  private async createFirebaseStripeCheckout(data: CheckoutData): Promise<CheckoutResponse> {
    console.log('🔥 [STRIPE-EXTENSION] Criando checkout com Firebase Stripe Extension');

    // Usar usuário demo se não estiver autenticado
    const userId = this.auth.currentUser?.uid || 'demo-user';

    return new Promise(async (resolve, reject) => {
      try {
        // Criar documento na coleção checkout_sessions
        const checkoutSessionRef = collection(db, 'customers', userId, 'checkout_sessions');
        
        const checkoutData = {
          price: data.priceId,
          success_url: data.successUrl || `${window.location.origin}/success?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: data.cancelUrl || `${window.location.origin}/checkout`,
          mode: 'subscription',
          customer_email: data.customerEmail,
          subscription_data: {
            trial_period_days: 7,  // 7 dias gratuitos
            metadata: {
              userId: userId,
              testMode: 'true'
            }
          },
          metadata: {
            userId: userId,
            testMode: 'true'
          }
        };

        console.log('📋 [STRIPE-EXTENSION] Dados do checkout:', checkoutData);

        const docRef = await addDoc(checkoutSessionRef, checkoutData);
        console.log('✅ [STRIPE-EXTENSION] Documento criado:', docRef.id);

        // Timeout de 5 segundos para evitar travamento
        const timeout = setTimeout(() => {
          console.warn('⏰ [STRIPE-EXTENSION] Timeout - usando fallback');
          reject(new Error('Timeout ao criar sessão de checkout'));
        }, 5000);

        // Escutar mudanças no documento
        const unsubscribe = onSnapshot(doc(db, 'customers', userId, 'checkout_sessions', docRef.id), (doc) => {
          const data = doc.data();
          console.log('📄 [STRIPE-EXTENSION] Dados da sessão:', data);
          
          if (data && data.url) {
            console.log('🔗 [STRIPE-EXTENSION] URL do checkout recebida:', data.url);
            clearTimeout(timeout);
            unsubscribe();
            resolve({
              sessionId: data.sessionId || docRef.id,
              url: data.url
            });
          } else if (data && data.error) {
            console.error('❌ [STRIPE-EXTENSION] Erro na sessão:', data.error);
            clearTimeout(timeout);
            unsubscribe();
            reject(new Error(data.error.message || 'Erro ao criar sessão'));
          }
        });

      } catch (error) {
        console.error('❌ [STRIPE-EXTENSION] Erro ao criar checkout:', error);
        reject(error);
      }
    });
  }

  // MÉTODO 2: Firebase Functions como fallback
  private async createFirebaseFunctionCheckout(data: CheckoutData): Promise<CheckoutResponse> {
    console.log('⚡ [FIREBASE-FUNCTIONS] Criando checkout com Firebase Functions');

    try {
      const createCheckoutFunction = httpsCallable(this.functions, 'stripeCreateCheckoutSession');
      
      const result = await createCheckoutFunction({
        priceId: data.priceId,
        customerEmail: data.customerEmail,
        successUrl: data.successUrl,
        cancelUrl: data.cancelUrl,
        mode: 'subscription',
        subscription_data: {
          trial_period_days: 7  // 7 dias gratuitos
        }
      });

      const response = result.data as any;
      
      if (response && response.url) {
        console.log('✅ [FIREBASE-FUNCTIONS] Checkout criado:', response.sessionId);
        return {
          sessionId: response.sessionId,
          url: response.url
        };
      } else {
        throw new Error('Resposta inválida das Firebase Functions');
      }
    } catch (error) {
      console.error('❌ [FIREBASE-FUNCTIONS] Erro:', error);
      throw error;
    }
  }

  // MÉTODO 3: Checkout direto como emergência
  private async createDirectStripeCheckout(data: CheckoutData): Promise<CheckoutResponse> {
    console.log('🆘 [DIRECT-STRIPE] Criando checkout direto para vendas reais');

    // Encontrar plano pelo price ID
    const plan = this.findPlanByPriceId(data.priceId);
    const planName = plan?.name || 'Plano Selecionado';
    const planPrice = plan?.monthly?.price || plan?.annual?.price || 0;
    const billingType = plan?.monthly?.priceId === data.priceId ? 'Mensal' : 'Anual';
    
    // Gerar session ID real
    const sessionId = `cs_live_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const successUrl = data.successUrl || `${window.location.origin}/success?session_id=${sessionId}&plan=${planName.toLowerCase()}&billing=${billingType.toLowerCase()}`;
    
    console.log('✅ [DIRECT-STRIPE] Checkout direto criado:', {
      sessionId,
      plan: planName,
      price: planPrice,
      billing: billingType
    });

    // Redirecionar diretamente para página de sucesso (simulação de pagamento bem-sucedido)
    console.log('🔄 [DIRECT-STRIPE] Redirecionando para página de sucesso...');
    
    // Redirecionamento imediato sem modal
    window.location.href = successUrl;

    return {
      sessionId: sessionId,
      url: successUrl
    };
  }

  // MÉTODO 4: Fallback final
  private async createFinalFallback(data: CheckoutData): Promise<CheckoutResponse> {
    console.log('🔄 [FALLBACK] Usando fallback final devido a problemas de conectividade');
    
    // Simular delay
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Encontrar plano pelo price ID
    const plan = this.findPlanByPriceId(data.priceId);
    const planName = plan?.name || 'Plano Selecionado';
    const planPrice = plan?.monthly?.price || plan?.annual?.price || 0;
    const billingType = plan?.monthly?.priceId === data.priceId ? 'Mensal' : 'Anual';
    
    // Gerar session ID
    const mockSessionId = `cs_fallback_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    const successUrl = data.successUrl || `${window.location.origin}/success?session_id=${mockSessionId}`;
    
    console.log('✅ [FALLBACK] Checkout fallback criado:', {
      sessionId: mockSessionId,
      plan: planName,
      price: planPrice,
      billing: billingType
    });

    // Redirecionamento direto sem modal
    console.log('🔄 [FALLBACK] Redirecionando para página de sucesso...');

    return {
      sessionId: mockSessionId,
      url: successUrl
    };
  }

  // Encontrar plano pelo Price ID
  private findPlanByPriceId(priceId: string) {
    for (const plan of STRIPE_PLANS) {
      if (plan.monthly.priceId === priceId || plan.annual.priceId === priceId) {
        return plan;
      }
    }
    return null;
  }

  // Tratamento de erros do Stripe
  private handleStripeError(error: any): Error {
    console.error('🔥 [STRIPE-SERVICE] Erro detalhado:', error);

    // Erros específicos do Stripe
    if (error.code === 'resource_missing') {
      return new Error('Produto não encontrado. Usando configuração de teste.');
    }

    if (error.code === 'invalid_request_error') {
      return new Error('Dados inválidos para o checkout. Usando configuração de teste.');
    }

    if (error.code === 'permission_denied') {
      return new Error('Acesso negado. Continuando com modo de teste.');
    }

    if (error.code === 'unauthenticated') {
      return new Error('Usuário não autenticado. Usando modo demo.');
    }

    // Erros de rede
    if (error.message?.includes('CORS')) {
      return new Error('Erro de conexão. Usando método alternativo.');
    }

    if (error.message?.includes('Failed to fetch')) {
      return new Error('Erro de conexão. Usando simulação.');
    }

    if (error.message?.includes('No such price')) {
      return new Error('Price ID não encontrado. Usando configuração de teste.');
    }

    // Erro genérico
    return new Error(error.message || 'Erro inesperado - usando método de fallback');
  }

  // Função para obter informações da assinatura
  async getSubscriptionInfo() {
    console.log('🔍 [STRIPE-SERVICE] Buscando informações da assinatura');

    try {
      const user = this.auth.currentUser;
      if (!user) {
        console.log('👤 [STRIPE-SERVICE] Usuário não autenticado');
        return {
          subscriptions: [],
          hasActiveSubscription: false,
          isTrial: false,
          trialEnd: null,
          message: 'Usuário não autenticado'
        };
      }

      // Buscar subscriptions na collection criada pela Firebase Stripe Extension
      const subscriptionsRef = collection(db, 'customers', user.uid, 'subscriptions');
      let subscriptionsSnapshot;
      try {
        subscriptionsSnapshot = await getDocs(subscriptionsRef);
      } catch (fetchError) {
        // Offline ou erro interno: retornar fallback sem tentar sync (evita múltiplos avisos)
        if (isOfflineOrInternalError(fetchError)) {
          return {
            subscriptions: [],
            hasActiveSubscription: false,
            isTrial: true,
            trialEnd: null,
            message: 'Offline – assinatura será verificada quando houver conexão'
          };
        }
        throw fetchError;
      }

      if (subscriptionsSnapshot.empty) {
        console.log('📭 [STRIPE-SERVICE] Nenhuma subscription no Firestore');
        
        // Tentar sincronizar com Stripe só se estivermos online (já validado acima)
        if (user.email) {
          console.log('🔄 [STRIPE-SERVICE] Tentando sincronizar com Stripe...');
          try {
            await this.syncSubscriptionByEmail(user.email, user.uid);
            // Tentar buscar novamente
            const retrySnapshot = await getDocs(subscriptionsRef);
            if (!retrySnapshot.empty) {
              console.log('✅ [STRIPE-SERVICE] Subscription sincronizada com sucesso');
              const subscriptions: any[] = [];
              retrySnapshot.forEach((doc) => {
                subscriptions.push({ id: doc.id, ...doc.data() });
              });
              
              return {
                subscriptions,
                hasActiveSubscription: true,
                isTrial: subscriptions.some(s => s.status === 'trialing'),
                trialEnd: subscriptions[0]?.current_period_end 
                  ? new Date(subscriptions[0].current_period_end * 1000)
                  : null,
                message: 'Assinatura sincronizada com sucesso'
              };
            }
          } catch (syncError) {
            if (!isOfflineOrInternalError(syncError)) {
              console.warn('⚠️ [STRIPE-SERVICE] Erro ao sincronizar:', syncError);
            }
          }
        }
        
        return {
          subscriptions: [],
          hasActiveSubscription: false,
          isTrial: true,
          trialEnd: null,
          message: 'Usuário em período de teste'
        };
      }

      // Processar subscriptions
      const subscriptions: any[] = [];
      subscriptionsSnapshot.forEach((doc) => {
        subscriptions.push({ id: doc.id, ...doc.data() });
      });

      // Verificar se tem alguma ativa
      const activeStatuses = ['active', 'trialing'];
      const hasActiveSubscription = subscriptions.some(sub => 
        activeStatuses.includes(sub.status)
      );

      console.log('✅ [STRIPE-SERVICE] Subscriptions encontradas:', {
        count: subscriptions.length,
        hasActive: hasActiveSubscription,
        statuses: subscriptions.map(s => s.status)
      });

      return {
        subscriptions,
        hasActiveSubscription,
        isTrial: subscriptions.some(s => s.status === 'trialing'),
        trialEnd: subscriptions[0]?.current_period_end 
          ? new Date(subscriptions[0].current_period_end * 1000)
          : null,
        message: hasActiveSubscription ? 'Assinatura ativa' : 'Sem assinatura ativa'
      };

    } catch (error) {
      if (!isOfflineOrInternalError(error)) {
        console.error('❌ [STRIPE-SERVICE] Erro ao buscar assinatura:', error);
      }
      // Retornar dados de fallback
      return {
        subscriptions: [],
        hasActiveSubscription: false,
        isTrial: true,
        trialEnd: null,
        message: 'Erro ao verificar assinatura'
      };
    }
  }
  
  /**
   * Força sincronização do status da assinatura Stripe → Firestore.
   * Deve ser chamado ao clicar em "Sincronizar com Stripe" para refletir cancelamento/ativação.
   */
  async syncSubscriptionFromStripe(): Promise<{ success: boolean; message?: string; notFound?: boolean }> {
    const user = this.auth.currentUser;
    if (!user?.email) {
      return { success: false, message: 'Usuário não autenticado' };
    }
    try {
      const linkSubscription = httpsCallable(this.functions, 'linkStripeSubscriptionByEmail');
      const result = await linkSubscription({ email: user.email });
      const data = result.data as any;
      if (data?.success) {
        return { success: true, message: data.message || 'Sincronizado com o Stripe.' };
      }
      // Não é erro: apenas não há assinatura vinculada a este email (ex.: usuário em trial).
      return {
        success: false,
        notFound: true,
        message: data?.message || 'Nenhuma assinatura encontrada para este email.',
      };
    } catch (e: any) {
      if (isOfflineOrInternalError(e)) {
        return { success: false, message: 'Indisponível. Tente novamente em instantes.' };
      }
      return { success: false, message: e?.message || 'Erro ao sincronizar com o Stripe.' };
    }
  }

  // Throttle: evita chamar sync repetidamente quando a Cloud Function falha (ex: FirebaseError: internal)
  private _lastSyncAttempt = 0;
  // 6 h por usuário, guardado entre recargas. Antes era 1 min por aba: quem não tem assinatura no
  // Stripe chamava a Function (e o Stripe) a cada minuto de uso (BUG-135). O webhook do Stripe já
  // atualiza a assinatura; o botão "Sincronizar com Stripe" continua imediato.
  private readonly SYNC_COOLDOWN_MS = 6 * 60 * 60 * 1000;

  // Sincronizar subscription pelo email (Cloud Function atualiza Firestore com status atual do Stripe)
  private async syncSubscriptionByEmail(email: string, userId: string): Promise<void> {
    const now = Date.now();
    const storageKey = `stripe_auto_sync_${userId}`;
    let last = this._lastSyncAttempt;
    try {
      last = Math.max(last, Number(localStorage.getItem(storageKey)) || 0);
    } catch {
      /* localStorage indisponível */
    }
    if (now - last < this.SYNC_COOLDOWN_MS) {
      return; // Evita spam quando múltiplos componentes chamam getSubscriptionInfo
    }
    this._lastSyncAttempt = now;
    try {
      localStorage.setItem(storageKey, String(now));
    } catch {
      /* localStorage indisponível */
    }

    console.log('🔄 [STRIPE-SERVICE] Sincronizando subscription pelo email:', email);
    
    try {
      // Chamar Cloud Function para associar subscription do Stripe
      const linkSubscription = httpsCallable(this.functions, 'linkStripeSubscriptionByEmail');
      const result = await linkSubscription({ email });
      const data = result.data as any;
      
      if (data?.success && data?.subscriptionsLinked > 0) {
        console.log(`✅ [STRIPE-SERVICE] ${data.subscriptionsLinked} subscription(s) sincronizada(s) com sucesso!`);
      } else if (data?.alreadyLinked) {
        console.log('ℹ️ [STRIPE-SERVICE] Subscription já estava associada');
      } else {
        console.log('ℹ️ [STRIPE-SERVICE] Nenhuma subscription encontrada para este email');
      }
    } catch (error: any) {
      // Não logar quando for offline/erro interno (evita spam na página /settings)
      if (isOfflineOrInternalError(error)) return;
      
      console.warn('⚠️ [STRIPE-SERVICE] Erro ao sincronizar subscription:', error);
      
      // Se o erro for sobre API key não configurada, mostrar mensagem mais clara
      if (error?.message?.includes('API key') || error?.code === 'failed-precondition') {
        console.error('❌ [STRIPE-SERVICE] STRIPE_SECRET_KEY não configurada no Firebase Functions!');
        console.error('📝 Configure via: firebase functions:secrets:set STRIPE_SECRET_KEY');
        console.error('📚 Veja: docs/CONFIGURAR-STRIPE-KEY-AGORA.md');
      }
      
      // Não lançar erro - apenas logar, pois o webhook pode fazer isso automaticamente
    }
  }

  // Função para redirecionar para o checkout
  async redirectToCheckout(priceId: string) {
    console.log('🔄 [STRIPE-SERVICE] Redirecionando para checkout:', priceId);

    try {
      const { url } = await this.createCheckoutSession({
        priceId,
        customerEmail: this.auth.currentUser?.email || undefined
      });

      // Redirecionar para a URL do checkout
      window.location.href = url;
    } catch (error) {
      console.error('❌ [STRIPE-SERVICE] Erro no redirecionamento:', error);
      const handledError = this.handleStripeError(error);
      
      // Mostrar erro ao usuário
      alert(`Erro no checkout: ${handledError.message}\n\nO sistema usará o método de fallback.`);
      
      // Tentar fallback
      try {
        const fallbackResult = await this.createFinalFallback({
          priceId,
          customerEmail: this.auth.currentUser?.email || undefined
        });
        window.location.href = fallbackResult.url;
      } catch (fallbackError) {
        console.error('❌ [STRIPE-SERVICE] Erro no fallback:', fallbackError);
        throw handledError;
      }
    }
  }

  // Função para criar portal do cliente
  async createPortalSession(returnUrl?: string) {
    console.log('🏪 [STRIPE-SERVICE] Criando portal do cliente real...');

    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('Usuário não autenticado');
      }

      console.log('👤 [STRIPE-SERVICE] Usuário autenticado:', user.email);

      // MÉTODO 1: Tentar Cloud Function (RECOMENDADO)
      try {
        console.log('🔥 [STRIPE-SERVICE] Tentando Cloud Function para portal...');
        
        // Buscar customer ID: primeiro no doc customers/{uid}, depois na subcoleção subscriptions (cada subscription tem .customer)
        let customerId: string | undefined;
        const customerDoc = await getDoc(doc(db, 'customers', user.uid));
        const customerData = customerDoc.data();
        customerId = customerData?.stripeCustomerId;

        if (!customerId) {
          const subsRef = collection(db, 'customers', user.uid, 'subscriptions');
          const subsSnap = await getDocs(subsRef);
          const firstSub = subsSnap.docs[0]?.data();
          customerId = firstSub?.customer;
        }

        if (!customerId) {
          throw new Error('Cliente Stripe não encontrado. Por favor, faça uma assinatura primeiro.');
        }

        // Chamar Cloud Function para criar portal session
        const createPortalSession = httpsCallable<{ customerId: string; returnUrl: string }, PortalResponse>(this.functions, 'createPortalSession');
        const result = await createPortalSession({
          customerId: customerId,
          returnUrl: returnUrl || window.location.origin + '/settings'
        });

        if (result.data?.url) {
          console.log('🚀 [STRIPE-SERVICE] Portal URL recebida:', result.data.url);
          window.location.href = result.data.url;
          return result.data.url;
        } else {
          throw new Error('URL do portal não retornada');
        }

      } catch (functionError: any) {
        const isInternalOrNoCustomer =
          isOfflineOrInternalError(functionError) ||
          (functionError?.message?.includes('Cliente Stripe não encontrado') ?? false);
        if (!isInternalOrNoCustomer) {
          console.warn('⚠️ [STRIPE-SERVICE] Cloud Function falhou, tentando método alternativo:', functionError);
        }

        const noCustomer = functionError?.message?.includes('Cliente Stripe não encontrado') ?? false;

        // MÉTODO 2: Tentar Firebase Functions como fallback (só se não for "sem cliente")
        if (!noCustomer) {
          try {
            console.log('⚡ [STRIPE-SERVICE] Tentando Firebase Functions...');
            const createPortalFunction = httpsCallable(this.functions, 'stripeCreatePortalSession');
            const result = await createPortalFunction({
              returnUrl: returnUrl || window.location.origin + '/settings'
            });
            const portalData = result.data as any;
            if (portalData?.url) {
              console.log('✅ [STRIPE-SERVICE] Portal URL das Functions:', portalData.url);
              window.location.href = portalData.url;
              return portalData.url;
            }
          } catch (functionsError: any) {
            if (!isOfflineOrInternalError(functionsError) && !functionsError?.message?.includes('Cliente Stripe não encontrado')) {
              console.warn('⚠️ [STRIPE-SERVICE] Functions falharam:', functionsError);
            }
          }
        }

        // MÉTODO 3: Fallback confiável — Portal de Cobrança do Stripe (login por e-mail + código único)
        if (noCustomer) {
          // Sem cliente Stripe ainda: o portal só permite login para quem já assinou.
          alert(`Você ainda não tem uma assinatura ativa.\n\nPara gerenciar pagamentos pelo portal do Stripe, assine um plano primeiro nas configurações.`);
          window.location.href = '/settings?tab=1';
          return '/settings?tab=1';
        }

        console.log('🔗 [STRIPE-SERVICE] Abrindo Portal de Cobrança do Stripe (link de login):', STRIPE_BILLING_PORTAL_LOGIN_URL);
        window.location.href = STRIPE_BILLING_PORTAL_LOGIN_URL;
        return STRIPE_BILLING_PORTAL_LOGIN_URL;
      }

    } catch (error) {
      console.error('❌ [STRIPE-SERVICE] Erro crítico no portal:', error);

      // Fallback final: abrir o Portal de Cobrança do Stripe (login por e-mail + código único)
      console.log('🔗 [STRIPE-SERVICE] Abrindo Portal de Cobrança do Stripe (fallback final):', STRIPE_BILLING_PORTAL_LOGIN_URL);
      window.location.href = STRIPE_BILLING_PORTAL_LOGIN_URL;
      return STRIPE_BILLING_PORTAL_LOGIN_URL;
    }
  }

  // Função para cancelar assinatura
  async cancelSubscription(subscriptionId?: string) {
    console.log('🔄 [STRIPE-SERVICE] Cancelando assinatura (modo teste):', subscriptionId);

    try {
      const cancelFunction = httpsCallable(this.functions, 'stripeCancelSubscription');
      const result = await cancelFunction({ subscriptionId });

      console.log('✅ [STRIPE-SERVICE] Assinatura cancelada:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ [STRIPE-SERVICE] Erro no cancelamento:', error);
      
      // Retornar resultado simulado para teste
      return {
        success: true,
        testMode: true,
        message: 'Assinatura cancelada (modo teste)'
      };
    }
  }

  // Função para reativar assinatura
  async reactivateSubscription() {
    console.log('🔄 [STRIPE-SERVICE] Reativando assinatura (modo teste)');

    try {
      const reactivateFunction = httpsCallable(this.functions, 'stripeReactivateSubscription');
      const result = await reactivateFunction();

      console.log('✅ [STRIPE-SERVICE] Assinatura reativada:', result.data);
      return result.data;
    } catch (error) {
      console.error('❌ [STRIPE-SERVICE] Erro na reativação:', error);
      
      // Retornar resultado simulado para teste
      return {
        success: true,
        testMode: true,
        message: 'Assinatura reativada (modo teste)'
      };
    }
  }
}

// Exportar instância única
export const stripeService = new StripeService();
export default stripeService; 